import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';

type ActionLogRow = {
  id: string;
  createdAt: string;
  eventType: string;
  storeId: string;
  storeDomain: string;
  metadata: Record<string, unknown>;
};

type ActionLogResponse = {
  rows: ActionLogRow[];
  total: number;
  note?: string;
};

const LIMIT = 50;

function formatDate(v?: string): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function actorFromMetadata(metadata: Record<string, unknown> | null | undefined): string {
  const m = (metadata || {}) as any;
  return m.actorEmail || m.userEmail || m.actorId || m.userId || '—';
}

export default function ActionLog() {
  const [domainInput, setDomainInput] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [offset, setOffset] = useState(0);

  const query = useQuery({
    queryKey: ['internal-action-log', domainFilter, offset, LIMIT],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (domainFilter.trim()) params.set('domain', domainFilter.trim().toLowerCase());
      params.set('limit', String(LIMIT));
      params.set('offset', String(offset));
      return apiFetch<ActionLogResponse>(`/api/internal/action-log?${params.toString()}`);
    },
  });

  const rows = query.data?.rows || [];
  const total = query.data?.total || 0;
  const hasPrev = offset > 0;
  const hasNext = offset + LIMIT < total;

  return (
    <div className="space-y-6 p-6">
      <div className="rounded-2xl border border-zinc-200 bg-white p-6">
        <h1 className="text-lg font-semibold text-zinc-900">Privileged Action Log</h1>
        <p className="mt-2 text-sm text-zinc-600">
          Showing data lifecycle events. Full staff action log requires Railway log drain (§3b of the production plan).
        </p>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-6">
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            placeholder="Filter by domain (optional)"
            className="w-full rounded-xl border border-zinc-300 px-3 py-2 text-sm"
          />
          <button
            onClick={() => {
              setOffset(0);
              setDomainFilter(domainInput.trim());
            }}
            className="rounded-xl bg-zinc-900 px-4 py-2 text-sm font-medium text-white"
          >
            Filter
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-6">
        {query.isLoading && <p className="text-sm text-zinc-500">Loading actions...</p>}
        {query.isError && <p className="text-sm text-red-600">Failed to load action log.</p>}
        {!query.isLoading && !query.isError && rows.length === 0 && (
          <p className="text-sm text-zinc-500">No actions logged yet. Actions appear here as staff use store controls.</p>
        )}

        {!query.isLoading && !query.isError && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-zinc-500">
                <tr className="border-b border-zinc-200">
                  <th className="px-3 py-2 font-medium">Timestamp</th>
                  <th className="px-3 py-2 font-medium">Actor</th>
                  <th className="px-3 py-2 font-medium">Action/Event</th>
                  <th className="px-3 py-2 font-medium">Store Domain</th>
                  <th className="px-3 py-2 font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-zinc-100 align-top">
                    <td className="px-3 py-2 text-zinc-700">{formatDate(row.createdAt)}</td>
                    <td className="px-3 py-2 text-zinc-700">{actorFromMetadata(row.metadata)}</td>
                    <td className="px-3 py-2 text-zinc-900">{row.eventType}</td>
                    <td className="px-3 py-2 text-zinc-700">{row.storeDomain || '—'}</td>
                    <td className="px-3 py-2 text-zinc-700">
                      <details>
                        <summary className="cursor-pointer text-xs text-zinc-600">View JSON</summary>
                        <pre className="mt-2 max-w-[560px] overflow-auto rounded bg-zinc-50 p-2 text-xs">
                          {JSON.stringify(row.metadata || {}, null, 2)}
                        </pre>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-zinc-500">
            Showing {rows.length === 0 ? 0 : offset + 1}-{offset + rows.length} of {total}
          </p>
          <div className="flex gap-2">
            <button
              disabled={!hasPrev}
              onClick={() => setOffset((v) => Math.max(0, v - LIMIT))}
              className="rounded-lg border border-zinc-300 px-3 py-1.5 text-xs font-medium text-zinc-700 disabled:opacity-50"
            >
              Prev
            </button>
            <button
              disabled={!hasNext}
              onClick={() => setOffset((v) => v + LIMIT)}
              className="rounded-lg border border-zinc-300 px-3 py-1.5 text-xs font-medium text-zinc-700 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
